// src/routes/PengaduanListPage.jsx
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx'; // npm i xlsx
import api from '../../services/apiClient';
import PengaduanCard from './PengaduanCard';
import dayjs from 'dayjs';

const API_BASE = `${process.env.REACT_APP_IRACE_API_URL}/api`;
const endpoint = '/indomaret/pengaduan/ikupon';

function mapRecord(r, i) {
  const imageUrl = r.file_url || r.file_name || r.imageUrl;
  return {
    id: r.id ?? r.pengaduan_id ?? i,
    nama: r.nama,
    email: r.email,
    telp: r.telp ?? r.contact,
    imageUrl,
    filename:
      r.filename ?? (imageUrl ? String(imageUrl).split('/').pop() : undefined),
    fileSize: r.file_size,
    createdAt: r.created_at || r.createdAt,
  };
}

function humanKB(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '';
  return Math.round(Number(bytes) / 1024);
}

function autosizeSheet(ws, rows) {
  const keys = rows.length ? Object.keys(rows[0]) : [];
  ws['!cols'] = keys.map((k) => {
    const maxLen = Math.max(
      k.length,
      ...rows.map((r) => String(r[k] ?? '').length)
    );
    return { wch: Math.min(Math.max(maxLen + 2, 8), 48) };
  });
}

export default function PengaduanListPage() {
  const [items, setItems] = useState([]); // semua data dari API
  const [page, setPage] = useState(1); // untuk tampilan saja (client-side)
  const [pageSize, setPageSize] = useState(12);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const resp = await api.get(endpoint, { baseURL: API_BASE });
      const data = Array.isArray(resp?.data) ? resp.data : [];
      setItems(data.map(mapRecord));
      setPage(1);
    } catch (e) {
      setErr(e?.message || 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // data untuk tampilan (slice)
  const currentItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(items.length / pageSize)),
    [items.length, pageSize]
  );

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleExportAllXls = async () => {
    if (!items.length) return;
    try {
      setExportBusy(true);

      const rows = items.map((r, i) => ({
        No: i + 1,
        Nama: r.nama ?? '',
        Email: r.email ?? '',
        Telepon: r.telp ?? '',
        // Filename: r.filename ?? '',
        // 'Ukuran (KB)': r.fileSize ? humanKB(r.fileSize) : '',
        'Created At':
          dayjs(dayjs(r.createdAt).subtract(7, 'hours')).format(
            'DD-MM-YYYY HH:mm:ss'
          ) ?? '',
        'Image URL': r.imageUrl ?? '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      autosizeSheet(ws, rows);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pengaduan');

      const now = new Date();
      const pad2 = (n) => String(n).padStart(2, '0');
      const fname = `pengaduan_full_${now.getFullYear()}-${pad2(
        now.getMonth() + 1
      )}-${pad2(now.getDate())}_${pad2(now.getHours())}${pad2(
        now.getMinutes()
      )}.xlsx`;

      XLSX.writeFile(wb, fname);
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mb: 2 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <Stack spacing={0.25}>
            <Typography variant="h5" fontWeight={800}>
              Daftar Pengaduan IKUPON
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Menampilkan pengaduan beserta foto struk yang diunggah.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="page-size-label">Per halaman</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Per halaman"
                onChange={handleChangePageSize}
              >
                {[8, 12, 16, 24, 32].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Export data ke Excel">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportAllXls}
                  disabled={exportBusy || loading || items.length === 0}
                >
                  {exportBusy ? 'Exporting…' : 'Export'}
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Muat ulang">
              <IconButton onClick={fetchData} disabled={loading || exportBusy}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {loading && !items.length ? (
        <Box py={8} display="grid" placeItems="center">
          <CircularProgress size={36} />
        </Box>
      ) : currentItems.length === 0 ? (
        <Box py={8} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Belum ada data pengaduan.
          </Typography>
        </Box>
      ) : (
        <>
          {/* GRID of cards */}
          <Grid container spacing={2} sx={{ mt: { xs: 1, md: 2 } }}>
            {currentItems.map((row, idx) => (
              <Grid
                key={row.id ?? row.imageUrl ?? idx}
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
              >
                <PengaduanCard data={row} />
              </Grid>
            ))}
          </Grid>

          {/* Pagination (client-side) */}
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="medium"
              shape="rounded"
            />
          </Box>
        </>
      )}
    </Container>
  );
}
